// src/riskFlow/ResidualRiskAssessment.js
import '@mantine/core/styles.css';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card, Text, Button, Stack, Group, Container, Alert, ActionIcon, Loader,
   Center, Textarea, Grid, Badge, Select
} from '@mantine/core';
import {
  IconAlertCircle, IconDeviceFloppy, IconEdit, IconRobot,
  IconCheck, IconX
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import {
  generateResidualRisk, updateResidualRisk, analyzeActionPlan,
  saveResidualRisk
} from '../redux/actions/residualRiskActions';
import { updateRiskPriority } from '../redux/actions/riskActions';
import { Navigation } from '../components/NavigationButtons';

/* ----------------------------------------------------------- */
/*  CONSTANTS & HELPERS                                        */
/* ----------------------------------------------------------- */

const RESIDUAL_RISK_LEVELS = [
  'Negligible', 'Low', 'Moderate', 'Significant', 'High', 'Critical'
];
const RESIDUAL_RISK_COLORS = [
  '#D8D8D8', '#B8C2B7', '#D4AA7D', '#B94D4D', '#993333', '#1A1A1A'
];

const ACTION_PLAN_LEVELS = [
  'Unsatisfactory', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'
];
const ACTION_PLAN_COLORS = [
  '#D32F2F', '#FF5722', '#FFC107', '#8BC34A', '#66BB6A', '#4CAF50'
];

const PRIORITY_LEVELS = RESIDUAL_RISK_LEVELS;
const riskPriorityColors = {
  Critical: '#1A1A1A',
  High: '#993333',
  Significant: '#B94D4D',
  Moderate: '#D4AA7D',
  Low: '#B8C2B7',
  Negligible: '#D8D8D8',
  None: '#EBEBEB'
};

// index helpers
const toIndex = (arr, txt = '', fallback = 0) =>
  Math.max(arr.findIndex(l => l.toLowerCase() === txt.toLowerCase()), fallback);
const cleanText = (s = '') => (typeof s === 'string' ? s.replace(/^\(|\)$/g, '').trim() : s);
const toStr = v => (typeof v === 'string' ? v : JSON.stringify(v, null, 2));

// select data
const residualRiskOptions = RESIDUAL_RISK_LEVELS.map(l => ({ value: l, label: l }));
const actionPlanOptions = ACTION_PLAN_LEVELS.map(l => ({ value: l, label: l }));
const priorityOptions = PRIORITY_LEVELS.map(l => ({ value: l, label: l }));

// coloured badge
const LevelBadge = ({ label, color }) => (
    <Badge size="lg" radius="sm" fw={600} color={color} variant="filled" tt="uppercase">
       {label}
     </Badge>
   );

/* ----------------------------------------------------------- */
/*  COMPONENT                                                  */
/* ----------------------------------------------------------- */
export default function ResidualRiskAssessment({
  modelId, assessmentUuid, riskArea, source, onNext, onBack, nextLabel = "Continue to Summary"
}) {
  const dispatch = useDispatch();

  /* ---------- selectors ---------- */
  const areaRisks = useSelector(s =>
    s.risks.byAssessment[assessmentUuid]?.modelRisks?.[riskArea]?.areaRisks?.[source.name] || []
  );
  const savedRisk = areaRisks.find(r => r.status === 'Saved');

  const residualRiskState = useSelector(
    s => s.residualRisk.byModel[modelId]?.[savedRisk?.riskId] || {}
  );

  const savedControls = useSelector(s => s.controls.savedControls || {});
  const savedControlEvaluations = useSelector(s => s.controls.savedControlEvaluations || {});

  // DEBUG: Log the data structures to understand the issue
  console.log('[ResidualRiskAssessment] DEBUG:', {
    riskId: savedRisk?.riskId,
    savedControls: savedControls,
    savedControlEvaluations: savedControlEvaluations,
    riskControls: savedControls[savedRisk?.riskId] || [],
    controlEvaluationsForRisk: savedControlEvaluations[savedRisk?.riskId] || {}
  });

  /* ---------- local state ---------- */
  const [isEditMode, setIsEditMode]   = useState(false);
  const [expanded, setExpanded]       = useState({ priority: false, actionPlan: false, residual: false });
  const [isSaved, setIsSaved]         = useState(false);
  const [edit, setEdit]               = useState({
    priority: '', priorityRationale: '',
    actionPlanRating: '', actionPlanRationale: '',
    residualRating: '', residualRationale: ''
  });

  /* ---------- effects ---------- */
  useEffect(() => { 
    const identifier = source.riskId || source.name || 'unknown';
    console.log('[ResidualRiskAssessment] Risk changed, resetting state for:', {
      identifier,
      riskId: source.riskId,
      riskName: source.riskName || source.title || source.name,
      sourceName: source.sourceName
    });
    setIsEditMode(false); 
    setExpanded({ priority:false,actionPlan:false,residual:false });
    setIsSaved(false); // Reset isSaved when switching risks
    setEdit({ // Reset edit state
      priority: '', priorityRationale: '',
      actionPlanRating: '', actionPlanRationale: '',
      residualRating: '', residualRationale: ''
    });
  }, [source.riskId, source.name, source.riskName, source.title, source.sourceName]);
  
  // Check if residual risk data exists and set isSaved accordingly
  useEffect(() => {
    const finalResidualRating =
      residualRiskState.overridden?.residual_rating ??
      residualRiskState.residual_rating ??
      'N/A';
    const finalActionPlanRating =
      residualRiskState.overridden?.action_plan_rating ??
      residualRiskState.action_plan_rating ??
      'N/A';
    
    const hasResidualData = finalResidualRating !== 'N/A' && finalActionPlanRating !== 'N/A';
    console.log('[ResidualRiskAssessment] Checking existing data:', {
      riskId: savedRisk?.riskId,
      finalResidualRating,
      finalActionPlanRating,
      hasResidualData
    });
    setIsSaved(hasResidualData);
  }, [residualRiskState, savedRisk?.riskId]);

  /* ---------- guards ---------- */
  if (!savedRisk) {
    return (
      <Container fluid px="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="No Saved Risk">
          Please save a risk before assessing residual risk.
        </Alert>
      </Container>
    );
  }

  /* ---------- prerequisite check ---------- */
  const riskControls = savedControls[savedRisk.riskId] || [];
  const controlEvaluations = savedControlEvaluations[savedRisk.riskId] || {};
  const prereq = () => {
    const missing = [];
    if (!savedRisk.priority) missing.push('Risk evaluation required');
    if (riskControls.length === 0) missing.push('No saved actions');
    else {
      // DEBUG: Check each control evaluation
      console.log('[ResidualRiskAssessment] Checking control evaluations:');
      riskControls.forEach(control => {
        const evaluations = controlEvaluations[control.controlId] || [];
        console.log(`- Control ${control.controlId}: ${evaluations.length} evaluations`, evaluations);
      });
      
      if (!riskControls.every(c => (controlEvaluations[c.controlId] || []).length > 0))
        missing.push('Some actions missing evaluations');
    }
    return { ok: missing.length === 0, reason: missing.join(', ') };
  };
  const { ok: canGen, reason: missing } = prereq();

  /* ---------- derived values ---------- */
  // Use new backend keys for mapping
  const finalResidualRating =
    residualRiskState.overridden?.residual_rating ??
    residualRiskState.residual_rating ??
    'N/A';
  const finalResidualExplain =
    residualRiskState.overridden?.residual_explanation ??
    residualRiskState.residual_explanation ??
    '';

  const finalActionPlanRating =
    residualRiskState.overridden?.action_plan_rating ??
    residualRiskState.action_plan_rating ??
    'N/A';
  const finalActionPlanExplain =
    residualRiskState.overridden?.action_plan_analysis ??
    residualRiskState.action_plan_analysis ??
    '';

  const rrIdx = toIndex(RESIDUAL_RISK_LEVELS, isEditMode ? edit.residualRating   : finalResidualRating, 0);
  const apIdx = toIndex(ACTION_PLAN_LEVELS,   isEditMode ? edit.actionPlanRating : finalActionPlanRating, 0);
  
  
  const priorityColor = riskPriorityColors[
    ((isEditMode ? edit.priority : savedRisk.priority) || '')
      .trim()
      .toLowerCase()
      .replace(/^\w/, c => c.toUpperCase())
  ] || riskPriorityColors.None;

  /* ---------- handlers ---------- */
  const handleGenerate = async () => {
    setIsSaved(false);
    try {
      await dispatch(analyzeActionPlan(modelId, savedRisk.riskId, savedRisk.riskName));
      await dispatch(generateResidualRisk(modelId, savedRisk.riskId));
      notifications.show({ title:'Generated', message:'Assessment updated', color:'green', icon:<IconCheck size={16}/> });
    } catch (err) {
      notifications.show({ title:'Error', message:err.message, color:'red' });
    }
  };

  const enterEdit = () => {
    setIsSaved(false);
    setEdit({
      priority: savedRisk.priority
        ? savedRisk.priority.charAt(0).toUpperCase() + savedRisk.priority.slice(1).toLowerCase()
        : '',
      priorityRationale: savedRisk.rationale || '',
      actionPlanRating: finalActionPlanRating === 'N/A' ? 'Unsatisfactory' : finalActionPlanRating,
      actionPlanRationale:
        typeof finalActionPlanExplain === 'string'
          ? finalActionPlanExplain
          : JSON.stringify(finalActionPlanExplain, null, 2),
      residualRating: finalResidualRating === 'N/A' ? 'Low' : finalResidualRating,
      residualRationale: finalResidualExplain || ''
    });
    setIsEditMode(true);
  };

  const saveEdits = async () => {
    if (edit.priority !== savedRisk.priority || edit.priorityRationale !== savedRisk.rationale) {
      await dispatch(
        updateRiskPriority(
          modelId,
          savedRisk.riskId,
          riskArea,
          source.name,
          edit.priority,
          assessmentUuid,
          edit.priorityRationale
        )
      );
    }
    dispatch(
      updateResidualRisk(modelId, savedRisk.riskId, {
        residual_rating: edit.residualRating,
        residual_explanation: edit.residualRationale,
        action_plan_rating: edit.actionPlanRating,
        action_plan_analysis: edit.actionPlanRationale,
      })
    );
    setIsEditMode(false);
    notifications.show({
      title: 'Saved',
      message: 'Changes stored locally',
      color: 'green',
      icon: <IconCheck size={16} />
    });
  };

  const saveAssessment = () => {
    dispatch(
      saveResidualRisk({
        modelId,
        assessmentUuid,
        riskId: savedRisk.riskId,
        riskName: savedRisk.riskName,
        priority: savedRisk.priority,
        residual_rating: finalResidualRating,
        residual_explanation: finalResidualExplain,
        action_plan_rating: finalActionPlanRating,
        action_plan_analysis: finalActionPlanExplain,
        action_plan_short_summary: '',
        evaluator_id: 'user@example.com',
        comments: ''
      })
    );
    setIsSaved(true);
    notifications.show({
      title: 'Accepted',
      message: 'Residual risk saved',
      color: 'green',
      icon: <IconDeviceFloppy size={16} />
    });
  };

  const toggleExp = key => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  // --- replace the old helper ---
  const trunc = (val, n = 70) => {
      const s = toStr(val ?? '');
      return s.length <= n ? s : s.slice(0, n) + '…';
    };

// just above render
const bothRatingsExist =
  finalResidualRating !== 'N/A' && finalActionPlanRating !== 'N/A';

  /* ---------- render ---------- */
  return (
    <Container fluid px="xl" py="xl">
      <Stack gap="lg">
        {/* header */}
        <Card withBorder p="xl">
          <Stack gap="xs">
            <Text size="sm" tt="uppercase" fw={700}>Residual Risk Assessment</Text>
            <Text size="lg" fw={600}>{savedRisk.refinedRisk || savedRisk.riskName}</Text>
            <Text size="sm" c="dimmed">Source: {source.name}</Text>
          </Stack>
        </Card>

        {/* prereq warning */}
        {!canGen && (
          <Alert icon={<IconAlertCircle size={16}/>} color="yellow" title="Prerequisites Missing">
            {missing}. Complete these before generating the assessment.
          </Alert>
        )}

        {/* assessment */}
        <Card withBorder p="xl">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Text c="dimmed" fw={600}>Residual Risk Analyzer</Text>
              {isEditMode ? (
                <Group gap="xs">
                  <Button size="sm" variant="subtle" leftSection={<IconX size={16}/>} onClick={()=>setIsEditMode(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" leftSection={<IconDeviceFloppy size={16}/>} onClick={saveEdits}>
                    Save
                  </Button>
                </Group>
              ) : (
                <Group gap="xs">
                  {canGen && (finalResidualRating !== 'N/A' || finalActionPlanRating !== 'N/A') && (
                    <ActionIcon 
                      variant="light" 
                      size="lg" 
                      onClick={handleGenerate}
                      disabled={residualRiskState.loading || residualRiskState.actionPlanLoading}
                      style={{ 
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        border: '1px solid rgba(25, 118, 210, 0.2)'
                      }}
                    >
                      {residualRiskState.loading || residualRiskState.actionPlanLoading ? (
                        <Loader size={16} />
                      ) : (
                        <IconRobot size={16} />
                      )}
                    </ActionIcon>
                  )}
                  {(savedRisk.priority || finalResidualRating!=='N/A' || finalActionPlanRating!=='N/A') &&
                    <ActionIcon variant="light" size="lg" onClick={enterEdit}>
                      <IconEdit size={16}/>
                    </ActionIcon>
                  }
                </Group>
              )}
            </Group>

            {/* labels */}
            <Grid columns={12} gutter="lg" pb="sm">
            <Grid.Col span={4}><Text size="md" fw={600} c="dark.7">Priority</Text></Grid.Col>
            <Grid.Col span={4}><Text size="md" fw={600} c="dark.7">Action Plan Effectiveness</Text></Grid.Col>
            <Grid.Col span={4}><Text size="md" fw={600} c="dark.7">Residual Risk</Text></Grid.Col>
          </Grid>

            {/* three cards */}
            <Grid columns={12} gutter="lg">

              {/* Priority */}
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ minHeight: isEditMode ? 360 : 200 }}>
                  <Stack gap="xs" align={isEditMode ? 'stretch' : 'flex-start'}>
                    <LevelBadge
                      label={isEditMode ? edit.priority : (savedRisk.priority || 'N/A')}
                      color={priorityColor}
                      style={{ marginTop: '8px' }}
                    />

                    {isEditMode && (
                      <>
                        <Select
                          data={priorityOptions}
                          value={edit.priority}
                          onChange={v => setEdit(e => ({ ...e, priority: v }))}
                          w="100%"
                          size="xs"
                          searchable
                          clearable={false}
                        />
                        <Textarea
                          value={edit.priorityRationale}
                          onChange={e =>
                            setEdit(p => ({ ...p, priorityRationale: e.currentTarget.value }))
                          }
                          placeholder="Explain the reasoning behind this priority level and why it's appropriate given the risk characteristics, potential impact, and organizational context..."
                          w="100%"
                          rows={8}
                          mt="lg"
                          size="xs"
                          style={{ flex: 1 }}
                        />
                      </>
                    )}

                    {!isEditMode && savedRisk.rationale && (
                      <Text
                        size="xs"
                          style={{ 
                          textAlign: 'left',
                          cursor: 'pointer',
                          marginTop: '12px',
                          padding: '8px',
                          backgroundColor: 'rgba(0,0,0,0.02)',
                          borderRadius: '4px'
                        }}
                        lineClamp={expanded.priority ? undefined : 7}
                        onClick={() => toggleExp('priority')}
                      >
                        {cleanText(
                          expanded.priority ? savedRisk.rationale : trunc(savedRisk.rationale)
                        )}
                      </Text>
                    )}
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Action-Plan */}
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ minHeight: isEditMode ? 360 : 200 }}>
                  <Stack gap="xs" align={isEditMode ? 'stretch' : 'flex-start'}>
                    <LevelBadge
                      label={
                        isEditMode
                          ? edit.actionPlanRating
                          : residualRiskState.overridden?.action_plan_rating || finalActionPlanRating
                      }
                      color={
                        finalActionPlanRating === 'N/A' && !isEditMode
                          ? '#CCC'
                          : ACTION_PLAN_COLORS[apIdx]
                      }
                      style={{ marginTop: '8px' }}
                    />

                    {isEditMode && (
                      <>
                        <Select
                          data={actionPlanOptions}
                          value={edit.actionPlanRating}
                          onChange={v => setEdit(e => ({ ...e, actionPlanRating: v }))}
                          w="100%"
                          size="xs"
                        />
                        <Textarea
                          value={edit.actionPlanRationale}
                          onChange={e =>
                            setEdit(p => ({ ...p, actionPlanRationale: e.currentTarget.value }))
                          }
                          placeholder="Evaluate the effectiveness of the proposed action plan including implementation feasibility, resource requirements, timeline considerations, and expected impact on risk mitigation..."
                          w="100%"
                          rows={8}
                          mt="lg"
                          size="xs"
                          style={{ flex: 1 }}
                        />
                      </>
                    )}

                    {!isEditMode && finalActionPlanExplain && (
                      <Text
                        size="xs"
                         style={{ 
                          textAlign: 'left',
                          cursor: 'pointer',
                          marginTop: '12px',
                          padding: '8px',
                          backgroundColor: 'rgba(0,0,0,0.02)',
                          borderRadius: '4px'
                        }}
                        lineClamp={expanded.actionPlan ? undefined : 7}
                        onClick={() => toggleExp('actionPlan')}
                      >
                        {cleanText(
                          expanded.actionPlan ? toStr(finalActionPlanExplain) : trunc(finalActionPlanExplain)
                        )}
                      </Text>
                    )}
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Residual */}
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ minHeight: isEditMode ? 360 : 200 }}>
                  <Stack gap="xs" align={isEditMode ? 'stretch' : 'flex-start'}>
                    <LevelBadge
                      label={
                        isEditMode
                          ? edit.residualRating
                          : residualRiskState.overridden?.residual_rating || finalResidualRating
                      }
                      color={
                        finalResidualRating === 'N/A' && !isEditMode
                          ? '#CCC'
                          : RESIDUAL_RISK_COLORS[rrIdx]
                      }
                      style={{ marginTop: '8px' }}
                    />

                    {isEditMode && (
                      <>
                        <Select
                          data={residualRiskOptions}
                          value={edit.residualRating}
                          onChange={v => setEdit(e => ({ ...e, residualRating: v }))}
                          w="100%"
                          size="xs"
                        />
                        <Textarea
                          value={edit.residualRationale}
                          onChange={e =>
                            setEdit(p => ({ ...p, residualRationale: e.currentTarget.value }))
                          }
                          placeholder="Assess the remaining risk level after implementing the action plan, considering residual vulnerabilities, implementation gaps, and ongoing threat landscape changes..."
                          w="100%"
                          rows={8}
                          mt="lg"
                          size="xs"
                          style={{ flex: 1 }}
                        />
                      </>
                    )}

                    {!isEditMode && finalResidualExplain && (
                      <Text
                        size="xs"
                        style={{ 
                          textAlign: 'left',
                          cursor: 'pointer',
                          marginTop: '12px',
                          padding: '8px',
                          backgroundColor: 'rgba(0,0,0,0.02)',
                          borderRadius: '4px'
                        }}
                        lineClamp={expanded.residual ? undefined : 7}
                        onClick={() => toggleExp('residual')}
                      >
                        {cleanText(
                          expanded.residual ? toStr(finalResidualExplain) : trunc(finalResidualExplain)
                        )}
                      </Text>
                    )}
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

              {/* update button */}
              {canGen && !isEditMode && !bothRatingsExist && (
                      <Center mt="xs">
                        <Button size="xs" leftSection={
                                residualRiskState.loading||residualRiskState.actionPlanLoading
                                  ? <Loader size={14}/> : <IconRobot size={14}/>}
                                onClick={handleGenerate}
                                disabled={residualRiskState.loading||residualRiskState.actionPlanLoading}>
                          Assess residual risk
                        </Button>
                      </Center>
                    )}

            {/* accept button */}
            {finalResidualRating!=='N/A' && finalActionPlanRating!=='N/A' && !isEditMode && (
              <Group justify="flex-end" mt="md">
                <Button leftSection={<IconDeviceFloppy size={16}/>} onClick={saveAssessment}>
                  Accept residual risk
                </Button>
              </Group>
            )}
          </Stack>
        </Card>

        {/* navigation */}
        <Navigation
          onBack={onBack}
          onNext={onNext}
          nextLabel={nextLabel}
          nextDisabled={!isSaved}
          showBack={!!onBack}
        />
      </Stack>
    </Container>
  );
}

